import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  CircleDot,
  Copy,
  Eye,
  ExternalLink,
  LoaderCircle,
  Network,
  Plus,
  RefreshCw,
  ShieldCheck,
  Unplug,
  WalletCards,
  X,
} from "lucide-react";
import { BLOCKDAG, isAddress, readPortfolio, shortenAddress } from "./network";

type Portfolio = {
  balance: string;
  block: number;
  nonce: number;
};

const STORE_KEY = "welshdag:watched-address";

function fingerprint(address: string) {
  const clean = address.toLowerCase().replace("0x", "");
  return Array.from({ length: 25 }, (_, index) => {
    const mirrored = index % 5 > 2 ? index - (index % 5) + (4 - (index % 5)) : index;
    const value = Number.parseInt(clean[(mirrored * 3) % clean.length] || "0", 16);
    return value % 3 !== 0;
  });
}

export default function App() {
  const [address, setAddress] = useState(() => localStorage.getItem(STORE_KEY) || "");
  const [draft, setDraft] = useState(address);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [showWatch, setShowWatch] = useState(false);
  const [showReceive, setShowReceive] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [walletAvailable, setWalletAvailable] = useState(Boolean(window.ethereum));
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const tiles = useMemo(() => fingerprint(address || `0x${"0".repeat(40)}`), [address]);

  const refresh = useCallback(async (target = address) => {
    if (!isAddress(target)) return;
    setLoading(true);
    setError("");
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 10_000);
    try {
      const next = await readPortfolio(target, controller.signal);
      setPortfolio(next);
      setLastUpdated(new Date());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not reach BlockDAG mainnet.");
    } finally {
      window.clearTimeout(timer);
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    setWalletAvailable(Boolean(window.ethereum));
    if (isAddress(address)) void refresh(address);
  }, [address, refresh]);

  useEffect(() => {
    if (!isAddress(address)) return;
    const timer = window.setInterval(() => void refresh(address), 30_000);
    return () => window.clearInterval(timer);
  }, [address, refresh]);

  useEffect(() => {
    if (!showWallet) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowWallet(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [showWallet]);

  function saveAddress(next: string) {
    const clean = next.trim();
    if (!isAddress(clean)) {
      setError("Enter a valid 42-character address beginning with 0x.");
      return;
    }
    localStorage.setItem(STORE_KEY, clean);
    setAddress(clean);
    setDraft(clean);
    setShowWatch(false);
    setError("");
  }

  async function connectWallet() {
    if (!window.ethereum) {
      setError("No browser wallet detected. You can still watch any public address.");
      setShowWatch(true);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const accounts = (await window.ethereum.request({ method: "eth_requestAccounts" })) as string[];
      if (!accounts?.[0] || !isAddress(accounts[0])) throw new Error("The wallet returned no valid address.");
      saveAddress(accounts[0]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Wallet connection was cancelled.");
    } finally {
      setLoading(false);
    }
  }

  async function addNetwork() {
    if (!window.ethereum) {
      setError("Install an EVM-compatible browser wallet to add BlockDAG automatically.");
      return;
    }
    try {
      await window.ethereum.request({
        method: "wallet_addEthereumChain",
        params: [{
          chainId: BLOCKDAG.chainIdHex,
          chainName: BLOCKDAG.name,
          nativeCurrency: { name: "BDAG", symbol: "BDAG", decimals: 18 },
          rpcUrls: [...BLOCKDAG.rpcUrls],
          blockExplorerUrls: [BLOCKDAG.explorerUrl],
        }],
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The network request was cancelled.");
    }
  }

  async function copyAddress() {
    if (!address) return;
    await navigator.clipboard.writeText(address);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  function forgetAddress() {
    localStorage.removeItem(STORE_KEY);
    setAddress("");
    setDraft("");
    setPortfolio(null);
    setError("");
  }

  return (
    <div className="app-shell">
      <div className="grain" aria-hidden="true" />
      <header className="topbar">
        <a className="brand" href="/" aria-label="WelshDAG Wallet home">
          <span className="flag-chip">
            <img src="https://welshdag.trade/assets/welsh-flag.svg" alt="Flag of Wales" />
          </span>
          <span className="brand-name">WelshDAG</span>
          <span className="beta">wallet.welshdag.trade</span>
        </a>
        <div className="topbar-actions">
          <span className="network-pill">
            <span className="live-dot" /> BLOCKDAG MAINNET
          </span>
          <button className="button primary compact" onClick={() => setShowWallet(true)}>
            <WalletCards size={15} /> Open wallet
          </button>
          <button className="button secondary compact" onClick={() => void addNetwork()}>
            <Plus size={15} /> Add network
          </button>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero-bg" aria-hidden="true" />
          <div className="hero-copy">
            <span className="eyebrow">BlockDAG · Mainnet Wallet</span>
            <h1><span className="accent">WelshDAG</span><span className="line2">Wallet</span></h1>
            <p>
              A non-custodial view of your BlockDAG wallet, built in Wales. Connect an
              existing browser wallet or watch any public address—your keys never enter this site.
            </p>
            <div className="hero-status">
              <span className="live-dot" />
              <strong>ONLINE</strong>
              <span>/</span>
              <span>chain 1404 verified</span>
            </div>
            {!address && (
              <div className="hero-actions">
                <button className="button primary" onClick={() => setShowWallet(true)}>
                  <WalletCards size={18} />
                  Open WelshDAG Wallet
                </button>
                <button className="button secondary" onClick={() => void connectWallet()} disabled={loading}>
                  {loading ? <LoaderCircle className="spin" size={18} /> : <Unplug size={18} />}
                  Connect another wallet
                </button>
                <button className="text-button" onClick={() => setShowWatch(true)}>
                  <Eye size={18} /> Watch an address <ChevronRight size={16} />
                </button>
              </div>
            )}
            {!walletAvailable && !address && (
              <span className="hint">No extension? Public-address mode works in every browser.</span>
            )}
          </div>
        </section>

        {error && (
          <div className="error-banner" role="alert">
            <CircleDot size={17} /> <span>{error}</span>
            <button aria-label="Dismiss error" onClick={() => setError("")}><X size={17} /></button>
          </div>
        )}

        {address ? (
          <section className="dashboard">
            <div className="balance-card">
              <div className="card-topline">
                <span>AVAILABLE BALANCE</span>
                <span className="read-only"><ShieldCheck size={15} /> READ-ONLY</span>
              </div>
              <div className="balance-row">
                <div>
                  <div className="balance">
                    {loading && !portfolio ? "—" : portfolio?.balance ?? "0.0000"}
                    <span>BDAG</span>
                  </div>
                  <p>Native balance on BlockDAG Mainnet</p>
                </div>
                <div className="identicon" aria-label="Address fingerprint">
                  {tiles.map((active, index) => <span key={index} className={active ? "on" : ""} />)}
                </div>
              </div>
              <div className="address-bar">
                <span className="address-label">ADDRESS</span>
                <code title={address}>{shortenAddress(address, 12, 10)}</code>
                <button onClick={() => void copyAddress()} aria-label="Copy address">
                  {copied ? <Check size={17} /> : <Copy size={17} />}
                </button>
              </div>
              <div className="wallet-actions">
                <button className="button primary" onClick={() => setShowReceive(true)}>
                  <ArrowUpRight size={18} /> Receive BDAG
                </button>
                <button className="button secondary" onClick={() => setShowWatch(true)}>
                  <Eye size={18} /> Watch another
                </button>
                <a className="button ghost" href={`${BLOCKDAG.explorerUrl}/address/${address}`} target="_blank" rel="noreferrer">
                  Explorer <ExternalLink size={16} />
                </a>
              </div>
            </div>

            <aside className="stats-panel">
              <div className="stat">
                <span>NETWORK STATUS</span>
                <strong><i className={portfolio ? "online" : ""} /> {portfolio ? "Operational" : "Checking"}</strong>
                <small>RPC chain verified as 1404</small>
              </div>
              <div className="stat">
                <span>LATEST BLOCK</span>
                <strong>{portfolio ? `#${portfolio.block.toLocaleString("en-GB")}` : "—"}</strong>
                <small>Live from BlockDAG RPC</small>
              </div>
              <div className="stat">
                <span>OUTBOUND NONCE</span>
                <strong>{portfolio?.nonce.toLocaleString("en-GB") ?? "—"}</strong>
                <small>Confirmed sent transaction count</small>
              </div>
              <button className="refresh" onClick={() => void refresh()} disabled={loading}>
                <RefreshCw className={loading ? "spin" : ""} size={16} />
                {loading ? "Refreshing mainnet…" : `Updated ${lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}`}
              </button>
            </aside>
          </section>
        ) : (
          <section className="empty-ledger">
            <div>
              <Network size={21} />
              <span>NETWORK</span>
              <strong>BlockDAG Mainnet</strong>
              <small>Chain ID 1404</small>
            </div>
            <div>
              <ShieldCheck size={21} />
              <span>CUSTODY</span>
              <strong>Keys stay with you</strong>
              <small>No seed phrase. Ever.</small>
            </div>
            <div>
              <Eye size={21} />
              <span>ACCESS</span>
              <strong>Public & private</strong>
              <small>Connect or watch</small>
            </div>
          </section>
        )}

        <section className="trust-strip">
          <div className="section-number">Why this wallet</div>
          <h2>Built for clarity, not custody</h2>
          <p className="section-lede">Everything the wallet shows is visible and verifiable—without asking you to trust it with your keys.</p>
          <div className="trust-grid">
            <article>
              <span>01</span>
              <ShieldCheck size={24} />
              <h3>Zero key custody</h3>
              <p>WelshDAG Wallet never asks for, sees, or stores private keys or recovery phrases.</p>
            </article>
            <article>
              <span>02</span>
              <Network size={24} />
              <h3>Verified mainnet</h3>
              <p>Every refresh checks the RPC-reported chain ID before showing account data.</p>
            </article>
            <article>
              <span>03</span>
              <Eye size={24} />
              <h3>Transparent by design</h3>
              <p>Balance data comes directly from the official RPC and links back to the explorer.</p>
            </article>
          </div>
        </section>
      </main>

      {showWallet && (
        <div className="wallet-overlay" role="dialog" aria-modal="true" aria-labelledby="embedded-wallet-title">
          <button className="wallet-overlay-backdrop" aria-label="Close wallet" onClick={() => setShowWallet(false)} />
          <section className="wallet-window">
            <header className="wallet-window-header">
              <div>
                <span>WELSHDAG SECURE WALLET</span>
                <strong id="embedded-wallet-title">Local encrypted vault</strong>
              </div>
              <div className="wallet-window-status"><i /> Chain 1404</div>
              <button onClick={() => setShowWallet(false)} aria-label="Close wallet"><X size={19} /></button>
            </header>
            <div className="wallet-web-notice">
              Your encrypted keystore is saved only in this browser. WelshDAG cannot recover your password or phrase.
            </div>
            <iframe
              src="/wallet.html"
              title="WelshDAG Wallet"
              allow="clipboard-write"
              referrerPolicy="no-referrer"
            />
          </section>
        </div>
      )}

      <footer>
        <div className="brand footer-brand">
          <span className="flag-chip">
            <img src="https://welshdag.trade/assets/welsh-flag.svg" alt="" />
          </span>
          <span className="brand-name">WelshDAG Wallet</span>
        </div>
        <p>Built in Wales for the BlockDAG community.</p>
        <div>
          <a href={BLOCKDAG.explorerUrl} target="_blank" rel="noreferrer">Explorer <ExternalLink size={13} /></a>
          {address && <button onClick={forgetAddress}><Unplug size={13} /> Forget address</button>}
        </div>
      </footer>

      {showWatch && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setShowWatch(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="watch-title" onMouseDown={(event) => event.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowWatch(false)} aria-label="Close"><X size={20} /></button>
            <span className="modal-kicker">PUBLIC ADDRESS MODE</span>
            <h2 id="watch-title">Watch a BlockDAG wallet</h2>
            <p>Paste a public 0x address. It stays in this browser and can never be used to spend funds.</p>
            <form onSubmit={(event: FormEvent) => { event.preventDefault(); saveAddress(draft); }}>
              <label htmlFor="wallet-address">BLOCKDAG ADDRESS</label>
              <input
                id="wallet-address"
                autoFocus
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="0x0000…"
                autoComplete="off"
                spellCheck={false}
              />
              <button className="button primary" type="submit"><Eye size={18} /> Open wallet view</button>
            </form>
          </div>
        </div>
      )}

      {showReceive && address && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setShowReceive(false)}>
          <div className="modal receive-modal" role="dialog" aria-modal="true" aria-labelledby="receive-title" onMouseDown={(event) => event.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowReceive(false)} aria-label="Close"><X size={20} /></button>
            <span className="modal-kicker">RECEIVE ON CHAIN 1404</span>
            <h2 id="receive-title">Receive BDAG</h2>
            <div className="large-identicon">
              {tiles.map((active, index) => <span key={index} className={active ? "on" : ""} />)}
            </div>
            <code>{address}</code>
            <button className="button primary" onClick={() => void copyAddress()}>
              {copied ? <Check size={18} /> : <Copy size={18} />} {copied ? "Address copied" : "Copy address"}
            </button>
            <p className="warning">Only send native BDAG on BlockDAG Mainnet to this address.</p>
          </div>
        </div>
      )}
    </div>
  );
}
